import React, { useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormControlLabel, {
    FormControlLabelProps,
    formControlLabelClasses,
} from '@mui/material/FormControlLabel';
import { colors } from 'common/constants';
import { Box, SxProps, Typography } from '@mui/material';
import BaseTextField from './base-text-field';
import { common } from '@mui/material/colors';

interface CheckboxProps extends Omit<FormControlLabelProps, 'control'> {
    name: string;
    helperText?: React.ReactNode;
    sxCheckbox?: SxProps;
}

export function BaseCheckbox({
    name,
    helperText,
    sxCheckbox,
    ...other
}: CheckboxProps) {
    const { control } = useFormContext();

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <div>
                    <FormControlLabel
                        control={
                            <Checkbox
                                {...field}
                                sx={{ ...sxCheckbox }}
                                checked={field.value}
                            />
                        }
                        {...other}
                    />

                    {(!!error || helperText) && (
                        <FormHelperText
                            error={!!error}
                            sx={{
                                fontSize: '14px',
                                color: `${colors.textError} !important`,
                                fontWeight: '400',
                            }}
                        >
                            {error ? error?.message : helperText}
                        </FormHelperText>
                    )}
                </div>
            )}
        />
    );
}

interface BaseMultiCheckboxProps
    extends Omit<FormControlLabelProps, 'control' | 'label'> {
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- options can be any
    options: { label: string; value: any; image?: string; type?: number }[];
    row?: boolean;
    label?: string;
    spacing?: number;
    helperText?: React.ReactNode;
    sxCheckbox?: SxProps;
}

export function BaseMultiCheckbox({
    row,
    name,
    label,
    options = [],
    spacing,
    helperText,
    sx,
    sxCheckbox,
    ...other
}: BaseMultiCheckboxProps) {
    const { control, getValues, setValue, watch } = useFormContext();
    const otherTextComponentName = `${name}OtherText`;
    const answerValue = watch(name);
    const optionType1 = options?.find((option) => option.type === 1);
    const [showOtherTextBox, setShowOtherTextBox] = React.useState(
        Number(getValues(name)) === optionType1?.value
    );

    useEffect(() => {
        if (!answerValue?.includes(optionType1?.value)) {
            setValue(otherTextComponentName, '');
        }
        setShowOtherTextBox(answerValue?.includes(optionType1?.value));
    }, [answerValue, optionType1?.value, otherTextComponentName, setValue]);

    const getSelected = (selectedItems: string[] = [], item: string) => {
        return selectedItems.includes(item)
            ? selectedItems.filter((value) => value !== item)
            : [...selectedItems, item];
    };
    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                    {label && (
                        <FormLabel
                            component="legend"
                            sx={{ typography: 'body2' }}
                        >
                            {label}
                        </FormLabel>
                    )}

                    <FormGroup
                        sx={{
                            ...(row && {
                                flexDirection: 'row',
                            }),
                            [`& .${formControlLabelClasses.root}`]: {
                                '&:not(:last-of-type)': {
                                    mb: spacing || 0,
                                },
                                ...(row && {
                                    mr: 0,
                                    '&:not(:last-of-type)': {
                                        mr: spacing || 2,
                                    },
                                }),
                            },
                            ...sx,
                        }}
                    >
                        {options.map((option) => (
                            <Box key={option.value}>
                                <FormControlLabel
                                    key={option.value}
                                    control={
                                        <Checkbox
                                            sx={{ ...sxCheckbox }}
                                            checked={field.value?.includes(
                                                option.value
                                            )}
                                            onChange={() =>
                                                field.onChange(
                                                    getSelected(
                                                        field.value,
                                                        option.value
                                                    )
                                                )
                                            }
                                        />
                                    }
                                    label={option.label}
                                    sx={{ ...sxCheckbox }}
                                    {...other}
                                />
                                {option.type === 1 && showOtherTextBox && (
                                    <BaseTextField
                                        name={otherTextComponentName}
                                        InputProps={{
                                            inputProps: {
                                                maxLength: 255,
                                            },
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                backgroundColor: common.white,
                                                borderRadius: '10px',
                                            },
                                        }}
                                    />
                                )}
                            </Box>
                        ))}
                    </FormGroup>

                    {(!!error || helperText) && (
                        <FormHelperText
                            error={!!error}
                            sx={{
                                fontSize: '14px',
                                marginLeft: 0,
                                color: `${colors.textError} !important`,
                                fontWeight: '400',
                            }}
                        >
                            {error ? error?.message : helperText}
                        </FormHelperText>
                    )}
                </FormControl>
            )}
        />
    );
}

export function BaseMultiCheckboxWithImage({
    name,
    label,
    options,
    helperText,
}: BaseMultiCheckboxProps) {
    const { control, getValues, setValue, watch } = useFormContext();
    const otherTextComponentName = `${name}OtherText`;
    const answerValue = watch(name);
    const optionType1 = options?.find((option) => option.type === 1);
    const [showOtherTextBox, setShowOtherTextBox] = React.useState(
        Number(getValues(name)) === optionType1?.value
    );

    useEffect(() => {
        if (!answerValue?.includes(optionType1?.value)) {
            setValue(otherTextComponentName, '');
        }
        setShowOtherTextBox(answerValue?.includes(optionType1?.value));
    }, [answerValue, optionType1?.value, otherTextComponentName, setValue]);

    const getSelected = (selectedItems: string[] = [], item: string) => {
        return selectedItems.includes(item)
            ? selectedItems.filter((value) => value !== item)
            : [...selectedItems, item];
    };
    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                    {label && (
                        <FormLabel
                            component="legend"
                            sx={{ typography: 'body2' }}
                        >
                            {label}
                        </FormLabel>
                    )}

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: [
                                'repeat(2, 1fr)',
                                'repeat(2, 1fr)',
                                'repeat(3, 1fr)',
                            ],
                            alignItems: 'stretch',
                            gap: [3, 5],
                            width: '100%',
                        }}
                    >
                        {options?.map((option, index) => {
                            const selected =
                                Array.isArray(field.value) &&
                                field.value.includes(option.value);
                            return (
                                <Box
                                    key={index}
                                    onClick={() =>
                                        field.onChange(
                                            getSelected(
                                                field.value,
                                                option.value
                                            )
                                        )
                                    }
                                    sx={{
                                        cursor: 'pointer',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: option.image
                                            ? 'flex-start'
                                            : 'center',
                                        gap: 1.5,
                                    }}
                                >
                                    {option.image && (
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                width: '100%',
                                                aspectRatio: '4 / 3',
                                                overflow: 'hidden',
                                                backgroundColor: '#F3F4F6',
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={option.image}
                                                alt={option.label}
                                                sx={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    display: 'block',
                                                }}
                                            />
                                        </Box>
                                    )}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <Checkbox
                                            checked={selected}
                                            readOnly
                                            tabIndex={-1}
                                            sx={{
                                                p: 0,
                                                pointerEvents: 'none',
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: [24, 32],
                                                },
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: [
                                                    '0.7rem',
                                                    '1rem',
                                                    '1rem',
                                                    '1.6rem',
                                                    '1.8rem',
                                                ],
                                                fontWeight: 500,
                                                color: selected
                                                    ? colors.main
                                                    : [
                                                          common.white,
                                                          common.black,
                                                      ],
                                                lineHeight: 1.35,
                                                overflowWrap: 'anywhere',
                                            }}
                                        >
                                            {option.label}
                                        </Typography>
                                    </Box>
                                    {option.type === 1 && showOtherTextBox && (
                                        <Box
                                            sx={{ pt: 0.5 }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <BaseTextField
                                                name={otherTextComponentName}
                                                InputProps={{
                                                    inputProps: {
                                                        maxLength: 255,
                                                    },
                                                }}
                                                sx={{
                                                    '& .MuiInputBase-root': {
                                                        backgroundColor:
                                                            common.white,
                                                        borderRadius: '10px',
                                                    },
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    {(!!error || helperText) && (
                        <FormHelperText
                            error={!!error}
                            sx={{
                                fontSize: '14px',
                                marginLeft: 0,
                                color: `${colors.textError} !important`,
                                fontWeight: '400',
                            }}
                        >
                            {error ? error?.message : helperText}
                        </FormHelperText>
                    )}
                </FormControl>
            )}
        />
    );
}
